/**
 * Vertical-aware curated segment taxonomy.
 *
 * Marketers don't think in integration paths — they think "Membership",
 * "Visits", "Spend". This file is the canonical taxonomy that maps those
 * marketer-facing labels to the integration-specific keys the segment engine
 * actually stores.
 *
 * Two verticals are curated today:
 * - **Fitness** (`SEGMENT_TAXONOMY` / `SEGMENT_CATEGORIES`) — gyms running
 *   Mariana Tek, Mindbody, or fitDEGREE. Mapped from Paper node `GL1-0`
 *   (Segments page) and cross-checked against `apps/api/scripts/afc/segments/*`.
 * - **Nightlife** (`NIGHTLIFE_TAXONOMY` / `NIGHTLIFE_CATEGORIES`) — venues
 *   running the Tablelist integration. Keys mirror what the Tablelist sync
 *   stores under `properties.tablelist.*` (see
 *   `apps/api/src/modules/integration-job/tablelist/customer.ts`).
 * - **Wellness** (`WELLNESS_TAXONOMY` / `WELLNESS_CATEGORIES`) — spas and
 *   wellness centers running Zenoti. Keys mirror the guest profile and
 *   history rollups the Zenoti sync stores under `properties.zenoti.*` (see
 *   `apps/api/src/modules/integration-job/zenoti/rollups.ts`).
 *
 * The active vertical is chosen at runtime from the gym's installed
 * integrations (see `resolveVertical` in `pick-source.ts`).
 *
 * Notes:
 * - Without an API-side `$or` fan-out we cannot persist multiple sibling
 *   keys for a single label. Instead, the form picks a single underlying
 *   source per gym at write time based on its installed/active integrations
 *   (see `pick-source.ts`).
 * - Anything not in this curated taxonomy is still reachable through the
 *   "All fields" / "All events" escape hatches (see `taxonomy-merge.ts`).
 */
import {
  Activity,
  AlignLeft,
  CalendarDaysIcon,
  DollarSignIcon,
  Hash,
  type LucideIcon,
  Tag,
  ToggleRight,
} from "lucide-react";
import type { IntegrationProviderId, PropertyType } from "@/api/types";

export type TaxonomyCategoryId =
  // Fitness
  | "membership"
  | "visits"
  | "journey"
  | "purchases"
  // Nightlife
  | "nightlife-activity"
  | "nightlife-spend"
  | "nightlife-status"
  // Wellness
  | "wellness-membership"
  | "wellness-visits"
  | "wellness-packages"
  | "wellness-spend"
  // Shared
  | "profile";

/** Curated vertical, selected from the gym's installed integrations. */
export type Vertical = "fitness" | "nightlife" | "wellness";

export interface TaxonomyCategory {
  id: TaxonomyCategoryId;
  label: string;
  description: string;
  order: number;
}

export const SEGMENT_CATEGORIES: TaxonomyCategory[] = [
  {
    id: "membership",
    label: "Membership",
    description:
      "Is the customer a paying member, what plan, where did they start, and have they joined or churned?",
    order: 0,
  },
  {
    id: "visits",
    label: "Visits & attendance",
    description: "Have they been in, when, how often — plus all the verb-shaped class events.",
    order: 1,
  },
  {
    id: "journey",
    label: "Journey",
    description:
      "Where are they in the funnel — lead, intro offer, drop-in, member, ClassPass user, lapsed?",
    order: 2,
  },
  {
    id: "purchases",
    label: "Purchases",
    description: "What did they buy, where, and how much have they spent?",
    order: 3,
  },
  {
    id: "profile",
    label: "Profile",
    description: "Identity, contact, and basic demographics.",
    order: 4,
  },
];

/**
 * Per-source mapping for a property-kind taxonomy field. `key` is the dotted
 * customer-property path the segment engine stores; `valueMap` (when present)
 * normalises canonical option values onto the per-integration spelling so
 * "active" → "Active" for Mindbody, etc.
 */
export interface TaxonomyPropertySource {
  key: string;
  valueMap?: Record<string, string>;
}

/**
 * Sub-condition seeded onto an event rule when the marketer picks a curated
 * event chip. Shape mirrors the persisted `ListRule` event condition so
 * `buildEventFromCurated` can copy it straight into the form.
 */
export interface TaxonomyEventCondition {
  /** Full event-property key, e.g. `properties.purchasables.type`. */
  key: string;
  type: PropertyType;
  operators: Array<{ op: string; value: unknown }>;
}

/**
 * Per-source mapping for an event-kind taxonomy field. `event` is the
 * `customer-event.event` string; `type` is the `customer-event.type`
 * (matches the integration provider id) used by the event-rule shape.
 */
export interface TaxonomyEventSource {
  event: string;
  type: IntegrationProviderId;
  /**
   * Optional sub-conditions prefilled when this curated entry is picked.
   * This is what lets one underlying event (e.g. `sale_completed`) back
   * multiple marketer-facing chips ("Purchased a class pack" =
   * `sale_completed` where product type is `pass`).
   */
  conditions?: TaxonomyEventCondition[];
}

interface TaxonomyFieldBase {
  /** Stable id, kebab-case. */
  id: string;
  /** Marketer-facing label. */
  label: string;
  /** Short hint shown under the label in the menu. */
  description?: string;
  /** Bucket the field appears in. */
  category: TaxonomyCategoryId;
  /** Type icon shown in the menu and on the chip. */
  icon: LucideIcon;
  /** Optional curated enum values for `select` / `multiselect` operators. */
  enumValues?: Array<{ label: string; value: string }>;
}

export interface TaxonomyPropertyField extends TaxonomyFieldBase {
  kind: "property";
  /** Drives operator and value pickers. */
  propertyType: PropertyType;
  /** Per-integration backing keys. */
  sources: Partial<Record<IntegrationProviderId, TaxonomyPropertySource>>;
}

export interface TaxonomyEventField extends TaxonomyFieldBase {
  kind: "event";
  /** Per-integration backing event names. */
  sources: Partial<Record<IntegrationProviderId, TaxonomyEventSource>>;
}

export type TaxonomyField = TaxonomyPropertyField | TaxonomyEventField;

/**
 * The canonical fitness-first taxonomy. Add new entries here when a new
 * integration property/event becomes worth surfacing to marketers without
 * editing the form code or constants.
 */
export const SEGMENT_TAXONOMY: TaxonomyField[] = [
  // ─── Membership ────────────────────────────────────────────────────────
  {
    id: "membership-status",
    label: "Membership status",
    description: "active · cancelled · frozen · payment_failure · expired",
    category: "membership",
    icon: AlignLeft,
    kind: "property",
    propertyType: "enum",
    enumValues: [
      { label: "Active", value: "active" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Frozen", value: "frozen" },
      { label: "Payment failure", value: "payment_failure" },
      { label: "Expired", value: "expired" },
    ],
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.membership.status" },
      mindbody: {
        key: "properties.mindbody.Status",
        valueMap: {
          active: "Active",
          cancelled: "Terminated",
          frozen: "Suspended",
          payment_failure: "Declined",
          expired: "Expired",
          non_member: "Non-Member",
        },
      },
    },
  },
  {
    id: "membership-name",
    label: "Membership name",
    description: "Unlimited · Founders Rate · 4x · 8x · VIP",
    category: "membership",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.membership.name" },
      mindbody: { key: "properties.mindbody.active_membership_name" },
      fitdegree: { key: "properties.fitdegree.memberships.title" },
    },
  },
  {
    id: "member-since",
    label: "Member since",
    description: "Membership start date",
    category: "membership",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.membership.start_date" },
      fitdegree: { key: "properties.fitdegree.memberships.start_at" },
    },
  },
  // FitDEGREE memberships are a binary active/inactive model (see
  // `deriveConsumableStatus` in the API), so they get a dedicated status
  // field rather than reusing the richer Mariana Tek / Mindbody enum.
  {
    id: "fd-membership-status",
    label: "Membership status",
    description: "active · inactive",
    category: "membership",
    icon: AlignLeft,
    kind: "property",
    propertyType: "enum",
    enumValues: [
      { label: "Active", value: "active" },
      { label: "Inactive", value: "inactive" },
    ],
    sources: {
      fitdegree: { key: "properties.fitdegree.memberships.status" },
    },
  },
  {
    id: "fd-membership-ends",
    label: "Membership ends",
    description: "Membership end / completion date",
    category: "membership",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      fitdegree: { key: "properties.fitdegree.memberships.end_at" },
    },
  },
  {
    id: "fd-membership-autopay",
    label: "Autopay enabled",
    description: "Membership renews automatically",
    category: "membership",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      fitdegree: { key: "properties.fitdegree.memberships.autopay_enabled" },
    },
  },
  {
    id: "fd-membership-payment-mode",
    label: "Payment mode",
    description: "How the membership is billed",
    category: "membership",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      fitdegree: { key: "properties.fitdegree.memberships.payment_mode" },
    },
  },
  {
    id: "fd-event-purchased-membership",
    label: "Purchased a membership",
    description: "Completed a sale that included a membership",
    category: "membership",
    icon: Activity,
    kind: "event",
    sources: {
      fitdegree: {
        event: "sale_completed",
        type: "fitdegree",
        conditions: [
          {
            key: "properties.purchasables.type",
            type: "string",
            operators: [{ op: "$eq", value: "membership" }],
          },
        ],
      },
    },
  },
  // Class packs ("pass" in the FitDEGREE API — marketers say "pack").
  {
    id: "fd-class-pack-status",
    label: "Class pack status",
    description: "active · inactive — any owned class pack",
    category: "membership",
    icon: AlignLeft,
    kind: "property",
    propertyType: "enum",
    enumValues: [
      { label: "Active", value: "active" },
      { label: "Inactive", value: "inactive" },
    ],
    sources: {
      fitdegree: { key: "properties.fitdegree.passes.status" },
    },
  },
  {
    id: "fd-class-pack-name",
    label: "Class pack name",
    description: 'e.g. "10-Class Pack", "Super 25"',
    category: "membership",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      fitdegree: { key: "properties.fitdegree.passes.title" },
    },
  },
  {
    id: "fd-class-pack-expires",
    label: "Class pack expires",
    description: 'Drives "pack expiring soon" follow-up flows',
    category: "membership",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      fitdegree: { key: "properties.fitdegree.passes.expiration_date" },
    },
  },
  {
    id: "fd-active-class-packs",
    label: "Active class packs",
    description: "How many class packs are currently active",
    category: "membership",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      fitdegree: { key: "properties.fitdegree.active_pass_count" },
    },
  },
  {
    id: "home-location",
    label: "Home location",
    description: "Where the membership was purchased",
    category: "membership",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.membership.purchase_location.name" },
      fitdegree: { key: "properties.fitdegree.home_location.name" },
    },
  },
  {
    id: "autopay-status",
    label: "Autopay status",
    description: "Active · Inactive · Suspended",
    category: "membership",
    icon: AlignLeft,
    kind: "property",
    propertyType: "enum",
    enumValues: [
      { label: "Active", value: "Active" },
      { label: "Inactive", value: "Inactive" },
      { label: "Suspended", value: "Suspended" },
    ],
    sources: {
      mindbody: { key: "properties.mindbody.MembershipStats.AutopayStatus" },
    },
  },
  // Mindbody memberships arrive as normalized rollups (see
  // `integration-job/mindbody/properties.ts`): booleans for active/annual
  // membership rather than the richer status enum other providers expose.
  {
    id: "mb-active-membership",
    label: "Active member",
    description: "Has a currently active membership or contract",
    category: "membership",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      mindbody: { key: "properties.mindbody.active_membership" },
    },
  },
  {
    id: "mb-annual-membership",
    label: "Annual member",
    description: "Active membership on an annual contract",
    category: "membership",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      mindbody: { key: "properties.mindbody.annual_membership" },
    },
  },
  {
    id: "event-started-membership",
    label: "Started membership",
    description: "Became a member — supports count + time window",
    category: "membership",
    icon: Activity,
    kind: "event",
    sources: {
      "mariana-tek": { event: "membership_activated", type: "mariana-tek" },
    },
  },
  {
    id: "event-ended-membership",
    label: "Ended membership",
    description: "Cancelled · lapsed · froze",
    category: "membership",
    icon: Activity,
    kind: "event",
    sources: {
      "mariana-tek": { event: "membership_deactivated", type: "mariana-tek" },
    },
  },

  // ─── Visits & attendance ──────────────────────────────────────────────
  {
    id: "total-visits",
    label: "Total visits",
    description: "Lifetime completed classes",
    category: "visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.completed_class_count" },
      mindbody: { key: "properties.mindbody.attended_class_count" },
      fitdegree: { key: "properties.fitdegree.completed_class_count" },
    },
  },
  {
    id: "last-visit",
    label: "Last visit",
    description: "Most recent check-in date",
    category: "visits",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.reservation.check_in_date" },
      mindbody: { key: "properties.mindbody.last_attended_at" },
      fitdegree: { key: "properties.fitdegree.last_class_taken_date" },
    },
  },
  {
    id: "next-class-scheduled",
    label: "Next class scheduled",
    description: "Earliest upcoming reservation",
    category: "visits",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      mindbody: { key: "properties.mindbody.next_class_at" },
      fitdegree: { key: "properties.fitdegree.next_class_date" },
    },
  },
  {
    id: "last-visit-status",
    label: "Last visit status",
    description: "check in · no show · cancelled",
    category: "visits",
    icon: AlignLeft,
    kind: "property",
    propertyType: "enum",
    enumValues: [
      { label: "Checked in", value: "check in" },
      { label: "No show", value: "no show" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Pending", value: "pending" },
    ],
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.reservation.status" },
    },
  },
  {
    id: "total-bookings",
    label: "Total bookings",
    description: "Lifetime reservations created",
    category: "visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.reservation_created_count" },
      mindbody: { key: "properties.mindbody.booked_class_count" },
      fitdegree: { key: "properties.fitdegree.registration_count" },
    },
  },
  {
    id: "late-cancellations",
    label: "Late cancellations",
    description: "Cancelled inside the late-cancel window",
    category: "visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      mindbody: { key: "properties.mindbody.late_cancelled_class_count" },
      fitdegree: { key: "properties.fitdegree.late_cancel_count" },
    },
  },
  {
    id: "credits-remaining",
    label: "Credits remaining",
    description: "Class-pack credits left",
    category: "visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.credit_payments_remaining" },
    },
  },
  {
    id: "mb-sessions-remaining",
    label: "Sessions remaining",
    description: "Sessions left on the current class pack",
    category: "visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      mindbody: { key: "properties.mindbody.pass_sessions_remaining" },
    },
  },
  // FitDEGREE's equivalent of "Credits remaining", named in the vocabulary
  // FitDEGREE gyms use ("classes left on the pack"). Summed across active
  // class packs by the API's `buildPassRollups`.
  {
    id: "fd-classes-remaining",
    label: "Classes remaining",
    description: "Classes left across active class packs",
    category: "visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      fitdegree: { key: "properties.fitdegree.pass_classes_remaining" },
    },
  },
  {
    id: "event-booked-class",
    label: "Booked a class",
    description: "Reservation created — not yet attended",
    category: "visits",
    icon: Activity,
    kind: "event",
    sources: {
      "mariana-tek": { event: "reservation_created", type: "mariana-tek" },
      mindbody: { event: "class_booked", type: "mindbody" },
      fitdegree: { event: "registration_created", type: "fitdegree" },
    },
  },
  {
    id: "event-checked-in",
    label: "Checked in to class",
    description: "Actually attended",
    category: "visits",
    icon: Activity,
    kind: "event",
    sources: {
      "mariana-tek": { event: "reservation_checkedin", type: "mariana-tek" },
      mindbody: { event: "class_attended", type: "mindbody" },
      fitdegree: { event: "registration_attended", type: "fitdegree" },
    },
  },
  {
    id: "event-no-showed",
    label: "No-showed a class",
    description: "Booked but didn’t show up",
    category: "visits",
    icon: Activity,
    kind: "event",
    sources: {
      "mariana-tek": { event: "reservation_noshowed", type: "mariana-tek" },
      mindbody: { event: "class_no_show", type: "mindbody" },
      fitdegree: { event: "registration_missed", type: "fitdegree" },
    },
  },
  {
    id: "event-cancelled-booking",
    label: "Cancelled a booking",
    description: "Cancelled before the class started",
    category: "visits",
    icon: Activity,
    kind: "event",
    sources: {
      "mariana-tek": { event: "reservation_cancelled", type: "mariana-tek" },
      mindbody: { event: "class_cancelled", type: "mindbody" },
      fitdegree: { event: "registration_cancelled", type: "fitdegree" },
    },
  },
  {
    id: "event-late-cancelled",
    label: "Late-cancelled a booking",
    description: "Inside the late-cancel window — separate signal",
    category: "visits",
    icon: Activity,
    kind: "event",
    sources: {
      mindbody: { event: "class_late_cancelled", type: "mindbody" },
      fitdegree: { event: "registration_late_cancelled", type: "fitdegree" },
    },
  },
  {
    id: "event-booked-appointment",
    label: "Booked an appointment",
    description: "1:1 service — PT, massage, etc.",
    category: "visits",
    icon: Activity,
    kind: "event",
    sources: {
      mindbody: { event: "appointment_booked", type: "mindbody" },
    },
  },

  // ─── Journey ──────────────────────────────────────────────────────────
  {
    id: "intro-offer-purchased-at",
    label: "Intro offer purchased on",
    description: "Date the intro offer was bought",
    category: "journey",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      mindbody: { key: "properties.mindbody.intro_offer_started_at" },
      fitdegree: { key: "properties.fitdegree.intro_offer_purchased_at" },
    },
  },
  {
    id: "intro-offer-ends-at",
    label: "Intro offer ends on",
    description: 'Drives "ending soon" follow-up flows',
    category: "journey",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      mindbody: { key: "properties.mindbody.intro_offer_ends_at" },
      fitdegree: { key: "properties.fitdegree.intro_offer_ends_at" },
    },
  },
  // Whether the intro ever led to a purchase, marked sticky by the API's
  // intro-conversion derivation. Backs "intro expired without converting"
  // segments (Mindbody-only; FitDEGREE conversion is inferred from sales).
  {
    id: "mb-intro-converted",
    label: "Converted from intro",
    description: "Bought a membership or pack after their intro offer",
    category: "journey",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      mindbody: { key: "properties.mindbody.intro_converted" },
    },
  },
  {
    id: "fd-signup-date",
    label: "Sign-up date",
    description: "When they were created in FitDEGREE",
    category: "journey",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      fitdegree: { key: "properties.fitdegree.created_at" },
    },
  },
  {
    id: "fd-first-class-date",
    label: "First class date",
    description: "Their earliest attended class",
    category: "journey",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      fitdegree: { key: "properties.fitdegree.first_class_date" },
    },
  },
  {
    id: "classpass-user",
    label: "ClassPass user",
    description: "External payment via ClassPass",
    category: "journey",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      fitdegree: { key: "properties.fitdegree.classpass_user" },
    },
  },
  {
    id: "event-intro-offer-purchased",
    label: "Purchased intro offer",
    description: "First trial purchase — the canonical funnel-entry signal",
    category: "journey",
    icon: Activity,
    kind: "event",
    sources: {
      fitdegree: { event: "intro_offer_purchased", type: "fitdegree" },
    },
  },
  {
    id: "event-viewed-page",
    label: "Viewed a page",
    description: "Opened a Helios landing page or booking page",
    category: "journey",
    icon: Activity,
    kind: "event",
    sources: {
      "helios-pixel": { event: "page_viewed", type: "helios-pixel" },
    },
  },
  {
    id: "event-submitted-form",
    label: "Submitted a form",
    description: "Completed a Helios form",
    category: "journey",
    icon: Activity,
    kind: "event",
    sources: {
      "helios-pixel": { event: "form_submitted", type: "helios-pixel" },
    },
  },
  {
    id: "event-helios-checkout",
    label: "Completed Helios checkout",
    description: "Finished checkout on a Helios booking page",
    category: "journey",
    icon: Activity,
    kind: "event",
    sources: {
      "helios-pixel": { event: "checkout_completed", type: "helios-pixel" },
    },
  },

  // ─── Purchases ────────────────────────────────────────────────────────
  {
    id: "last-product-purchased",
    label: "Last product purchased",
    description: 'e.g. "5 Pack", "Founders", intro offer SKU',
    category: "purchases",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.order.product" },
    },
  },
  {
    id: "last-purchase-location",
    label: "Last purchase location",
    description: "Studio where the order was placed",
    category: "purchases",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.order.location" },
    },
  },
  {
    id: "last-purchase-date",
    label: "Last purchase date",
    description: "Date the most recent order was placed",
    category: "purchases",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.order.date_placed" },
      mindbody: { key: "properties.mindbody.last_purchase_at" },
      fitdegree: { key: "properties.fitdegree.last_purchase_at" },
    },
  },
  {
    id: "lifetime-spend",
    label: "Lifetime spend",
    description: "Net amount paid (paid − refunded)",
    category: "purchases",
    icon: DollarSignIcon,
    kind: "property",
    propertyType: "currency",
    sources: {
      fitdegree: { key: "properties.fitdegree.total_spend_cents" },
    },
  },
  {
    id: "fd-products-purchased",
    label: "Products purchased",
    description: "Every product name they've ever bought",
    category: "purchases",
    icon: Tag,
    kind: "property",
    propertyType: "array",
    sources: {
      fitdegree: { key: "properties.fitdegree.purchased_names" },
    },
  },
  {
    id: "event-made-purchase",
    label: "Made a purchase",
    description: "Order completed (any product)",
    category: "purchases",
    icon: Activity,
    kind: "event",
    sources: {
      "mariana-tek": { event: "order_completed", type: "mariana-tek" },
      mindbody: { event: "order_completed", type: "mindbody" },
      fitdegree: { event: "sale_completed", type: "fitdegree" },
    },
  },
  {
    id: "fd-event-purchased-class-pack",
    label: "Purchased a class pack",
    description: "Completed a sale that included a class pack",
    category: "purchases",
    icon: Activity,
    kind: "event",
    sources: {
      fitdegree: {
        event: "sale_completed",
        type: "fitdegree",
        conditions: [
          {
            key: "properties.purchasables.type",
            type: "string",
            operators: [{ op: "$eq", value: "pass" }],
          },
        ],
      },
    },
  },
  {
    id: "event-refunded-purchase",
    label: "Refunded a purchase",
    description: "Sale (or part of a sale) was reversed",
    category: "purchases",
    icon: Activity,
    kind: "event",
    sources: {
      mindbody: { event: "order_refunded", type: "mindbody" },
      fitdegree: { event: "sale_refunded", type: "fitdegree" },
    },
  },

  // ─── Profile ──────────────────────────────────────────────────────────
  {
    id: "profile-email",
    label: "Email",
    description: "Base customer field",
    category: "profile",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      helios: { key: "email" },
    },
  },
  {
    id: "profile-phone",
    label: "Phone",
    description: "Base customer field",
    category: "profile",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      helios: { key: "phoneNumber" },
    },
  },
  {
    id: "profile-first-name",
    label: "First name",
    description: "Base customer field",
    category: "profile",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      helios: { key: "firstName" },
    },
  },
  {
    id: "profile-last-name",
    label: "Last name",
    description: "Base customer field",
    category: "profile",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      helios: { key: "lastName" },
    },
  },
  {
    id: "profile-birthday",
    label: "Birthday",
    description: "Ordinal — today, in the next N days, in the last N days",
    category: "profile",
    icon: CalendarDaysIcon,
    kind: "property",
    // The ordinal operator overlay is keyed off the property's key path,
    // not its declared type — see ORDINAL_PROPERTIES in `constants/lists.ts`.
    propertyType: "number",
    sources: {
      "mariana-tek": { key: "properties.mariana_tek.birth_date_ordinal" },
      fitdegree: { key: "properties.fitdegree.birthdate_ordinal" },
      tablelist: { key: "properties.tablelist.birthdayOrdinal" },
      zenoti: { key: "properties.zenoti.date_of_birth_ordinal" },
    },
  },
  // Helios-native tags (`customer.properties.tags`) — applied manually or by
  // automations/flows. Backed by the always-on `helios` source so the field is
  // available to every gym regardless of installed integrations.
  {
    id: "profile-tags",
    label: "Tags",
    description: "Tags applied in Helios — manually or by an automation",
    category: "profile",
    icon: Tag,
    kind: "property",
    propertyType: "array",
    sources: {
      helios: { key: "properties.tags" },
    },
  },
  {
    id: "profile-fitdegree-tags",
    label: "FitDEGREE tags",
    description: "Tags synced from FitDEGREE",
    category: "profile",
    icon: Tag,
    kind: "property",
    propertyType: "array",
    sources: {
      fitdegree: { key: "properties.fitdegree.tags" },
    },
  },
];

/**
 * Profile fields are vertical-agnostic (base Helios identity + birthday) and
 * are shared by every curated vertical. Sourced from the fitness taxonomy so
 * there's a single definition to maintain.
 */
const PROFILE_FIELDS: TaxonomyField[] = SEGMENT_TAXONOMY.filter(
  (field) => field.category === "profile",
);

export const NIGHTLIFE_CATEGORIES: TaxonomyCategory[] = [
  {
    id: "nightlife-activity",
    label: "Visits & reservations",
    description: "How often they come in, when they last visited, and reservation activity.",
    order: 0,
  },
  {
    id: "nightlife-spend",
    label: "Spend",
    description: "How much they've spent across tables, tickets, and guest list.",
    order: 1,
  },
  {
    id: "nightlife-status",
    label: "Status & tags",
    description: "VIP status and marketing opt-in.",
    order: 2,
  },
  {
    id: "profile",
    label: "Profile",
    description: "Identity, contact, and basic demographics.",
    order: 3,
  },
];

/**
 * The curated nightlife taxonomy for Tablelist venues. Keys mirror the shape
 * persisted by the Tablelist sync under `properties.tablelist.*`.
 */
export const NIGHTLIFE_TAXONOMY: TaxonomyField[] = [
  // ─── Visits & reservations ────────────────────────────────────────────
  {
    id: "nl-event-made-reservation",
    label: "Made a reservation",
    description: "Booked or visited — table, guest list, or ticket",
    category: "nightlife-activity",
    icon: Activity,
    kind: "event",
    sources: {
      tablelist: { event: "reservation", type: "tablelist" },
    },
  },
  {
    id: "nl-event-viewed-page",
    label: "Viewed a page",
    description: "Opened a Helios landing page or booking page",
    category: "nightlife-activity",
    icon: Activity,
    kind: "event",
    sources: {
      "helios-pixel": { event: "page_viewed", type: "helios-pixel" },
    },
  },
  {
    id: "nl-event-submitted-form",
    label: "Submitted a form",
    description: "Completed a Helios form",
    category: "nightlife-activity",
    icon: Activity,
    kind: "event",
    sources: {
      "helios-pixel": { event: "form_submitted", type: "helios-pixel" },
    },
  },
  {
    id: "nl-event-helios-checkout",
    label: "Completed Helios checkout",
    description: "Finished checkout on a Helios booking page",
    category: "nightlife-activity",
    icon: Activity,
    kind: "event",
    sources: {
      "helios-pixel": { event: "checkout_completed", type: "helios-pixel" },
    },
  },
  {
    id: "nl-total-visits",
    label: "Total visits",
    description: "Lifetime visits across all reservation types",
    category: "nightlife-activity",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      tablelist: { key: "properties.tablelist.stats.totalVisits" },
    },
  },
  {
    id: "nl-last-visit",
    label: "Last visit",
    description: "Most recent visit date",
    category: "nightlife-activity",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      tablelist: { key: "properties.tablelist.stats.latestVisit" },
    },
  },
  {
    id: "nl-first-visit",
    label: "First visit",
    description: "Date of their first visit",
    category: "nightlife-activity",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      tablelist: { key: "properties.tablelist.stats.firstVisit" },
    },
  },
  {
    id: "nl-table-visits",
    label: "Table visits",
    description: "Lifetime table reservations",
    category: "nightlife-activity",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      tablelist: { key: "properties.tablelist.stats.totalTableVisits" },
    },
  },
  {
    id: "nl-guestlist-visits",
    label: "Guest list visits",
    description: "Lifetime guest list entries",
    category: "nightlife-activity",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      tablelist: { key: "properties.tablelist.stats.totalGuestlistVisits" },
    },
  },
  {
    id: "nl-ticket-visits",
    label: "Ticket visits",
    description: "Lifetime ticketed entries",
    category: "nightlife-activity",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      tablelist: { key: "properties.tablelist.stats.totalTicketVisits" },
    },
  },

  // ─── Spend ────────────────────────────────────────────────────────────
  {
    id: "nl-lifetime-spend",
    label: "Lifetime spend",
    description: "Total amount spent across all visits",
    category: "nightlife-spend",
    icon: DollarSignIcon,
    kind: "property",
    propertyType: "currency",
    sources: {
      tablelist: { key: "properties.tablelist.stats.totalSpend" },
    },
  },
  {
    id: "nl-average-spend",
    label: "Average spend",
    description: "Average spend per visit",
    category: "nightlife-spend",
    icon: DollarSignIcon,
    kind: "property",
    propertyType: "currency",
    sources: {
      tablelist: { key: "properties.tablelist.stats.averageSpend" },
    },
  },
  {
    id: "nl-table-spend",
    label: "Table spend",
    description: "Total spent on table reservations",
    category: "nightlife-spend",
    icon: DollarSignIcon,
    kind: "property",
    propertyType: "currency",
    sources: {
      tablelist: { key: "properties.tablelist.stats.totalTableSpend" },
    },
  },

  // ─── Status & tags ────────────────────────────────────────────────────
  {
    id: "nl-vip-status",
    label: "VIP",
    description: "Flagged as a VIP guest",
    category: "nightlife-status",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      tablelist: { key: "properties.tablelist.vip" },
    },
  },
  {
    id: "nl-marketing-opt-in",
    label: "Marketing opt-in",
    description: "Opted in to marketing communications",
    category: "nightlife-status",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      tablelist: { key: "properties.tablelist.marketingOptIn" },
    },
  },
  {
    id: "nl-tags",
    label: "Tablelist tags",
    description: 'Venue, event, and visit tags — e.g. "Visited - Scorpion Bar Boston"',
    category: "nightlife-status",
    icon: Tag,
    kind: "property",
    propertyType: "array",
    sources: {
      tablelist: { key: "properties.tablelist.tags" },
    },
  },

  // ─── Profile (shared) ─────────────────────────────────────────────────
  ...PROFILE_FIELDS,
];

export const WELLNESS_CATEGORIES: TaxonomyCategory[] = [
  {
    id: "wellness-membership",
    label: "Membership",
    description: "Are they a member, on which plan, when it renews, and how many credits are left?",
    order: 0,
  },
  {
    id: "wellness-visits",
    label: "Visits & appointments",
    description: "How often they come in, what they book, who they see, and what's coming up.",
    order: 1,
  },
  {
    id: "wellness-packages",
    label: "Packages",
    description: "Prepaid service packages, sessions left, and when they expire.",
    order: 2,
  },
  {
    id: "wellness-spend",
    label: "Spend",
    description: "How much they've spent across services, memberships, and packages.",
    order: 3,
  },
  {
    id: "profile",
    label: "Profile",
    description: "Identity, contact, consent, and basic demographics.",
    order: 4,
  },
];

/**
 * Curated taxonomy for Zenoti spas and wellness centers. Property keys are
 * the guest profile and history rollups on `properties.zenoti.*`; appointment
 * events are shared with Mindbody, which emits the same event names.
 */
export const WELLNESS_TAXONOMY: TaxonomyField[] = [
  // ─── Membership ───────────────────────────────────────────────────────
  {
    id: "zn-is-member",
    label: "Member",
    description: "Holds a current membership",
    category: "wellness-membership",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      zenoti: { key: "properties.zenoti.is_member" },
    },
  },
  {
    id: "zn-membership-name",
    label: "Membership plan",
    description: 'e.g. "Monthly Massage", "Glow Club"',
    category: "wellness-membership",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      zenoti: { key: "properties.zenoti.membership_name" },
    },
  },
  {
    id: "zn-membership-status",
    label: "Membership status",
    description: "active · frozen · cancelled · expired",
    category: "wellness-membership",
    icon: AlignLeft,
    kind: "property",
    propertyType: "enum",
    enumValues: [
      { label: "Active", value: "active" },
      { label: "Frozen", value: "frozen" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Expired", value: "expired" },
      { label: "Inactive", value: "inactive" },
      { label: "Not started", value: "not_started" },
      { label: "Suspended", value: "suspended" },
      { label: "Closed", value: "closed" },
      { label: "Refunded", value: "refunded" },
      { label: "Payment failed", value: "failed" },
    ],
    sources: {
      zenoti: { key: "properties.zenoti.membership_status" },
    },
  },
  {
    id: "zn-membership-type",
    label: "Membership type",
    description: "recurring · prepaid",
    category: "wellness-membership",
    icon: AlignLeft,
    kind: "property",
    propertyType: "enum",
    enumValues: [
      { label: "Recurring", value: "recurring" },
      { label: "Prepaid", value: "prepaid" },
    ],
    sources: {
      zenoti: { key: "properties.zenoti.membership_type" },
    },
  },
  {
    id: "zn-membership-started",
    label: "Membership started",
    description: "Start date of the current membership",
    category: "wellness-membership",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.membership_started_at" },
    },
  },
  {
    id: "zn-became-member",
    label: "First became a member",
    description: "Start date of their earliest membership",
    category: "wellness-membership",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.became_member_at" },
    },
  },
  {
    id: "zn-membership-expires",
    label: "Membership expires",
    description: "End date of the current membership",
    category: "wellness-membership",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.membership_expires_at" },
    },
  },
  {
    id: "zn-membership-next-billing",
    label: "Next billing date",
    description: "When the recurring membership bills next",
    category: "wellness-membership",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.membership_next_billing_at" },
    },
  },
  {
    id: "zn-membership-cancelled",
    label: "Membership cancelled on",
    description: "When the current membership was cancelled",
    category: "wellness-membership",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.membership_cancelled_at" },
    },
  },
  {
    id: "zn-membership-price",
    label: "Membership price",
    description: "Amount billed per cycle",
    category: "wellness-membership",
    icon: DollarSignIcon,
    kind: "property",
    propertyType: "currency",
    sources: {
      zenoti: { key: "properties.zenoti.membership_price" },
    },
  },
  {
    id: "zn-credits-left",
    label: "Credits left",
    description: "Unused membership service credits",
    category: "wellness-membership",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      zenoti: { key: "properties.zenoti.membership_credits_left" },
    },
  },
  {
    id: "zn-credits-expire",
    label: "Credits expire",
    description: "When the unused membership credits expire",
    category: "wellness-membership",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.membership_credits_expire_at" },
    },
  },
  {
    id: "zn-event-membership-started",
    label: "Started a membership",
    category: "wellness-membership",
    icon: Activity,
    kind: "event",
    sources: {
      zenoti: { event: "membership_started", type: "zenoti" },
    },
  },
  {
    id: "zn-event-membership-cancelled",
    label: "Cancelled a membership",
    category: "wellness-membership",
    icon: Activity,
    kind: "event",
    sources: {
      zenoti: { event: "membership_cancelled", type: "zenoti" },
    },
  },

  // ─── Visits & appointments ────────────────────────────────────────────
  {
    id: "zn-visit-count",
    label: "Visits",
    description: "Completed appointments",
    category: "wellness-visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      zenoti: { key: "properties.zenoti.visit_count" },
    },
  },
  {
    id: "zn-first-visit",
    label: "First visit",
    description: "Date of their first completed appointment",
    category: "wellness-visits",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.first_visit_at" },
    },
  },
  {
    id: "zn-last-visit",
    label: "Last visit",
    description: "Date of their most recent completed appointment",
    category: "wellness-visits",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.last_visit_at" },
    },
  },
  {
    id: "zn-next-appointment",
    label: "Next appointment",
    description: "Date of their next booked appointment",
    category: "wellness-visits",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.next_appointment_at" },
    },
  },
  {
    id: "zn-upcoming-appointments",
    label: "Upcoming appointments",
    description: "Booked appointments still ahead",
    category: "wellness-visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      zenoti: { key: "properties.zenoti.upcoming_appointment_count" },
    },
  },
  {
    id: "zn-no-shows",
    label: "No-shows",
    description: "Appointments they missed",
    category: "wellness-visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      zenoti: { key: "properties.zenoti.no_show_count" },
    },
  },
  {
    id: "zn-cancellations",
    label: "Cancellations",
    description: "Appointments they cancelled",
    category: "wellness-visits",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      zenoti: { key: "properties.zenoti.cancelled_count" },
    },
  },
  {
    id: "zn-top-service",
    label: "Top service",
    description: "The service they book most",
    category: "wellness-visits",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      zenoti: { key: "properties.zenoti.top_service" },
    },
  },
  {
    id: "zn-preferred-therapist",
    label: "Preferred therapist",
    description: "The therapist they see most",
    category: "wellness-visits",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      zenoti: { key: "properties.zenoti.preferred_therapist" },
    },
  },
  {
    id: "zn-event-booked-appointment",
    label: "Booked an appointment",
    category: "wellness-visits",
    icon: Activity,
    kind: "event",
    sources: {
      mindbody: { event: "appointment_booked", type: "mindbody" },
      zenoti: { event: "appointment_booked", type: "zenoti" },
    },
  },
  {
    id: "zn-event-attended-appointment",
    label: "Attended an appointment",
    category: "wellness-visits",
    icon: Activity,
    kind: "event",
    sources: {
      mindbody: { event: "appointment_attended", type: "mindbody" },
      zenoti: { event: "appointment_attended", type: "zenoti" },
    },
  },
  {
    id: "zn-event-no-show-appointment",
    label: "No-showed an appointment",
    category: "wellness-visits",
    icon: Activity,
    kind: "event",
    sources: {
      mindbody: { event: "appointment_no_show", type: "mindbody" },
      zenoti: { event: "appointment_no_show", type: "zenoti" },
    },
  },
  {
    id: "zn-event-cancelled-appointment",
    label: "Cancelled an appointment",
    category: "wellness-visits",
    icon: Activity,
    kind: "event",
    sources: {
      mindbody: { event: "appointment_cancelled", type: "mindbody" },
      zenoti: { event: "appointment_cancelled", type: "zenoti" },
    },
  },

  // ─── Packages ─────────────────────────────────────────────────────────
  {
    id: "zn-package-count",
    label: "Active packages",
    description: "Packages with sessions left; above 0 means they have one",
    category: "wellness-packages",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      zenoti: { key: "properties.zenoti.package_count" },
    },
  },
  {
    id: "zn-package-sessions-left",
    label: "Package sessions left",
    description: "Unused sessions across active packages",
    category: "wellness-packages",
    icon: Hash,
    kind: "property",
    propertyType: "number",
    sources: {
      zenoti: { key: "properties.zenoti.package_sessions_left" },
    },
  },
  {
    id: "zn-package-expires",
    label: "Package expires",
    description: "Earliest expiry across active packages",
    category: "wellness-packages",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      zenoti: { key: "properties.zenoti.package_expires_at" },
    },
  },
  {
    id: "zn-package-names",
    label: "Package names",
    description: "Names of their active packages",
    category: "wellness-packages",
    icon: Tag,
    kind: "property",
    propertyType: "array",
    sources: {
      zenoti: { key: "properties.zenoti.package_names" },
    },
  },
  {
    id: "zn-event-package-purchased",
    label: "Purchased a package",
    category: "wellness-packages",
    icon: Activity,
    kind: "event",
    sources: {
      zenoti: { event: "package_purchased", type: "zenoti" },
    },
  },

  // ─── Spend ────────────────────────────────────────────────────────────
  {
    id: "zn-lifetime-spend",
    label: "Lifetime spend",
    description: "Services, memberships, and packages combined",
    category: "wellness-spend",
    icon: DollarSignIcon,
    kind: "property",
    propertyType: "currency",
    sources: {
      zenoti: { key: "properties.zenoti.lifetime_spend" },
    },
  },

  // ─── Profile ──────────────────────────────────────────────────────────
  {
    id: "zn-guest-since",
    label: "Guest since",
    description: "When they were created in Zenoti",
    category: "profile",
    icon: CalendarDaysIcon,
    kind: "property",
    propertyType: "date",
    sources: {
      helios: { key: "createdAt" },
    },
  },
  {
    id: "zn-home-center",
    label: "Home center",
    description: "Their Zenoti home center",
    category: "profile",
    icon: Tag,
    kind: "property",
    propertyType: "string",
    sources: {
      zenoti: { key: "properties.zenoti.center_name" },
    },
  },
  {
    id: "zn-marketing-texts",
    label: "Opted in to marketing texts",
    description: "Marketing text preference from Zenoti",
    category: "profile",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      zenoti: { key: "properties.zenoti.preferences.receive_marketing_sms" },
    },
  },
  {
    id: "zn-marketing-email",
    label: "Opted in to marketing email",
    description: "Marketing email preference from Zenoti",
    category: "profile",
    icon: ToggleRight,
    kind: "property",
    propertyType: "boolean",
    sources: {
      zenoti: { key: "properties.zenoti.preferences.receive_marketing_email" },
    },
  },
  ...PROFILE_FIELDS,
];

/**
 * Resolve the curated taxonomy + categories for a vertical.
 */
export function getVerticalTaxonomy(vertical: Vertical): {
  taxonomy: TaxonomyField[];
  categories: TaxonomyCategory[];
} {
  if (vertical === "nightlife") {
    return { taxonomy: NIGHTLIFE_TAXONOMY, categories: NIGHTLIFE_CATEGORIES };
  }
  if (vertical === "wellness") {
    return { taxonomy: WELLNESS_TAXONOMY, categories: WELLNESS_CATEGORIES };
  }
  return { taxonomy: SEGMENT_TAXONOMY, categories: SEGMENT_CATEGORIES };
}

/**
 * Look up a curated taxonomy field by id, across every vertical. Returns
 * undefined for unknown ids (the form falls through to the "All fields" /
 * "All events" escape hatches).
 */
export function getTaxonomyField(id: string): TaxonomyField | undefined {
  return (
    SEGMENT_TAXONOMY.find((field) => field.id === id) ??
    NIGHTLIFE_TAXONOMY.find((field) => field.id === id) ??
    WELLNESS_TAXONOMY.find((field) => field.id === id)
  );
}

/**
 * Group a curated taxonomy by category for menu rendering. `categories`
 * controls both which buckets appear and their order.
 */
export function groupTaxonomyByCategory(
  fields: TaxonomyField[] = SEGMENT_TAXONOMY,
  categories: TaxonomyCategory[] = SEGMENT_CATEGORIES,
): Array<{ category: TaxonomyCategory; fields: TaxonomyField[] }> {
  const byCategory = new Map<TaxonomyCategoryId, TaxonomyField[]>();
  for (const field of fields) {
    const bucket = byCategory.get(field.category) ?? [];
    bucket.push(field);
    byCategory.set(field.category, bucket);
  }
  return categories
    .map((category) => ({
      category,
      fields: byCategory.get(category.id) ?? [],
    }))
    .filter((entry) => entry.fields.length > 0);
}
